import { CheckCircle, Shield, Sms } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { PLAN_SELECTION_PATH } from '@/constants/pages';
import {
  IRIS_EMAIL,
  IRIS_MAILTO_HREF,
  IRIS_PHONE_DISPLAY,
  IRIS_SMS_HREF
} from '@/constants/iris';
import { ANALYTICS_EVENTS, captureEvent } from '@/lib/analytics';
import { tDynamic } from '@/lib/i18n';
import { brandVars as brand } from '@/theme/theme';
import Link from 'next/link';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

export const LandingHero: FunctionComponent = () => {
  const { t } = useTranslation();

  const claims = [
    t('landing:hero.claims.fastVerdict'),
    t('landing:hero.claims.noAccount'),
    t('landing:hero.claims.bothChannels')
  ];

  return (
    <Box
      component="section"
      sx={{
        pt: { xs: 12, sm: 16 },
        pb: { xs: 6, sm: 10 },
        px: { xs: 2, sm: 3 }
      }}
    >
      <Container maxWidth="lg">
        <Grid
          container
          spacing={{ xs: 4, lg: 6 }}
          sx={{ alignItems: 'center' }}
        >
          <Grid size={{ xs: 12, lg: 6 }}>
            <Stack sx={{ gap: 3 }}>
              <Chip
                icon={<Shield sx={{ fontSize: 16 }} />}
                label={t('landing:hero.chip')}
                sx={(theme) => ({
                  bgcolor: brand.colors.blue50,
                  color: 'primary.dark',
                  ...theme.applyStyles('dark', {
                    bgcolor: 'rgba(96, 165, 250, 0.12)',
                    color: 'var(--mui-palette-primary-light)'
                  }),
                  fontWeight: 500,
                  alignSelf: 'flex-start',
                  '& .MuiChip-icon': { color: 'primary.dark' }
                })}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: {
                    xs: '2rem',
                    sm: '2.5rem',
                    lg: '3rem',
                    xl: '3.5rem'
                  },
                  lineHeight: 1.15,
                  fontWeight: 600
                }}
              >
                {t('landing:hero.title')}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem', lg: '1.25rem' },
                  color: 'text.secondary',
                  maxWidth: 560
                }}
              >
                {t('landing:hero.subtitle')}
              </Typography>

              <Box
                sx={(theme) => ({
                  // `brand.heroTipGradient` is mode-aware via the palette,
                  // so CSS variables handle the light/dark swap automatically.
                  background: 'var(--mui-palette-brand-heroTipGradient)',
                  border: '1px solid',
                  borderColor: brand.colors.blue200,
                  ...theme.applyStyles('dark', {
                    borderColor: 'var(--mui-palette-divider)'
                  }),
                  borderRadius: 2,
                  p: 2.5
                })}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.primary', mb: 2, fontWeight: 500 }}
                >
                  {t('landing:hero.tryNow')}
                </Typography>
                <Stack sx={{ gap: 1.5 }}>
                  <Button
                    component="a"
                    href={IRIS_SMS_HREF}
                    variant="contained"
                    size="medium"
                    startIcon={<Sms />}
                    sx={{ alignSelf: 'flex-start', fontWeight: 500 }}
                  >
                    {tDynamic(t, 'landing:hero.textCta', {
                      phone: IRIS_PHONE_DISPLAY
                    })}
                  </Button>
                  <Box
                    component="a"
                    href={IRIS_MAILTO_HREF}
                    sx={{
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                      display: 'inline-flex',
                      alignItems: 'center',
                      minHeight: 44,
                      alignSelf: 'flex-start'
                    }}
                  >
                    {tDynamic(t, 'landing:hero.emailLink', {
                      email: IRIS_EMAIL
                    })}
                  </Box>
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', mt: 1.5, display: 'block' }}
                >
                  {t('landing:hero.freeNoCard')}
                </Typography>
              </Box>

              <Stack
                sx={{
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  mt: 1
                }}
              >
                <Button
                  component={Link}
                  href={PLAN_SELECTION_PATH}
                  onClick={() =>
                    captureEvent(ANALYTICS_EVENTS.SIGNUP_CTA_CLICKED, {
                      location: 'hero'
                    })
                  }
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 500
                  }}
                >
                  {t('landing:hero.getStartedFree')}
                </Button>
                <Button
                  component="a"
                  href="#how-it-works"
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: 'grey.400',
                      borderWidth: 2,
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  {t('landing:hero.seeHowItWorks')}
                </Button>
              </Stack>

              <Stack
                sx={{ flexDirection: 'row', gap: 1, mt: 1, flexWrap: 'wrap' }}
              >
                {claims.map((claim) => (
                  <Chip
                    key={claim}
                    icon={<CheckCircle sx={{ fontSize: 14 }} />}
                    label={claim}
                    size="small"
                    sx={(theme) => ({
                      bgcolor: 'grey.100',
                      color: 'text.secondary',
                      ...theme.applyStyles('dark', {
                        bgcolor: 'var(--mui-palette-background-subtle)'
                      }),
                      fontSize: '0.75rem',
                      '& .MuiChip-icon': { color: 'success.main' }
                    })}
                  />
                ))}
              </Stack>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Box sx={{ position: 'relative', mt: { xs: 4, lg: 0 } }}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: brand.gradients.cta,
                  borderRadius: 4,
                  transform: 'rotate(3deg)'
                }}
              />
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1706806593440-53c9dab6d39f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhZHVsdCUyMGRhdWdodGVyJTIwaGVscGluZyUyMGVsZGVybHklMjBtb3RoZXIlMjB0ZWNobm9sb2d5fGVufDF8fHx8MTc3ODUyNTUwNnww&ixlib=rb-4.1.0&q=80&w=1080"
                alt={t('landing:hero.imageAlt')}
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  boxShadow: 24,
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
